/**
 * Login page — split layout.
 *
 *   Left: dark brand panel with Configural mark, tagline, feature list
 *   Right: email + password form
 */

import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../AuthContext.jsx";
import { ManageCareMark } from "../components.jsx";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      const redirectTo = loc.state?.from?.pathname || "/";
      nav(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-split">
      {/* ─── Left brand panel ─────────────────────────────── */}
      <aside className="auth-brand-panel">
        <div className="auth-brand-top">
          <div className="auth-brand-logo">
            <ManageCareMark size={28} />
          </div>
          <span className="auth-brand-name">Configural</span>
        </div>

        <div className="auth-brand-body">
          <h1 className="auth-brand-headline">
            Configure production voice agents without shipping code.
          </h1>
          <p className="auth-brand-tagline">
            Design, version, and govern the prompts, tools, and playbooks
            behind every conversation. One library, every tenant, full audit
            trail.
          </p>

          <ul className="auth-brand-features">
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Per-organization workspaces</strong>
                <p>Isolated library, tools, and tenants for every customer.</p>
              </div>
            </li>
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Version-controlled prompts</strong>
                <p>Draft → Review → Prod with full diff history and revert.</p>
              </div>
            </li>
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Multi-agent orchestration</strong>
                <p>Compose agents with tool registries and linked playbooks.</p>
              </div>
            </li>
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Reusable content library</strong>
                <p>Share prompts, tools, and protocols across every agent.</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="auth-brand-footer">
          <span>© 2026 Configural</span>
          <span className="auth-brand-dot">·</span>
          <a href="#privacy">Privacy</a>
          <span className="auth-brand-dot">·</span>
          <a href="#terms">Terms</a>
        </div>
      </aside>

      {/* ─── Right form panel ─────────────────────────────── */}
      <main className="auth-form-panel">
        <div className="auth-form-card">
          <div className="auth-form-mobile-brand">
            <ManageCareMark size={30} />
            <span>Configural</span>
          </div>

          <div className="auth-form-header">
            <h2>Sign in to your workspace</h2>
            <p className="muted small">
              Welcome back. Enter your email and password to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <label className="auth-field">
              <span className="auth-field-label">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                placeholder="you@company.com"
                autoComplete="email"
              />
            </label>

            <label className="auth-field">
              <div className="auth-field-label-row">
                <span className="auth-field-label">Password</span>
                <a
                  href="#forgot"
                  className="auth-forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    alert(
                      "Password reset is not wired up yet. Reset directly in the database."
                    );
                  }}
                >
                  Forgot?
                </a>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </label>

            <label className="auth-remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              Keep me signed in
            </label>

            {error && (
              <div className="auth-error-banner">
                <span className="auth-error-dot" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="auth-submit"
              disabled={busy}
            >
              {busy ? (
                <>
                  <span className="auth-spinner" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="auth-form-footer">
            Don&apos;t have an organization yet?{" "}
            <Link to="/signup">Create one</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
